/* eslint-disable react/jsx-props-no-spreading */
import {
  Stack, TextField, Button, Alert, AlertTitle,
} from '@mui/material';
import {
  LoadingButton,
} from '@mui/lab';
import React from 'react';
import {
  getFormProps, getFieldProps, getSubmitButtonProps, useForm,
} from '@marageti/z4-lib';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

export type FormProps = {
  onSubmit: (values: any) => void;
  onCancel: () => void;
  defaultValues?: {
    street1?: string;
    street2?: string;
    country?: string;
    state?: string;
    city?: string;
    zipcode?: string;
  }
  submissionError: boolean;
};

const schema = z.object({
  street1: z.string().min(1, 'Required'),
  street2: z.string().optional(),
  country: z.string().min(1, 'Required'),
  state: z.string().min(1, 'Required'),
  city: z.string().min(1, 'Required'),
  zipcode: z.string().min(1, 'Required'),
});

const DEFAULT_VALUES = {
  street1: '',
  street2: '',
  country: '',
  state: '',
  city: '',
  zipcode: '',
};

const Form = ({
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionError,
}: FormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  return (
    <form {...getFormProps(handleSubmit(onSubmit))}>
      <Stack useFlexGap>
        <TextField
          {...register('street1')}
          {...getFieldProps('street1', errors)}
          fullWidth
          label="Street Address 1"
          required
          variant="outlined"
        />
        <TextField
          {...register('street2')}
          {...getFieldProps('street2', errors)}
          fullWidth
          label="Street Address 2"
          variant="outlined"
        />
        <TextField
          {...register('country')}
          {...getFieldProps('country', errors)}
          fullWidth
          label="Country"
          required
          variant="outlined"
        />
        <TextField
          {...register('state')}
          {...getFieldProps('state', errors)}
          fullWidth
          label="State / Province"
          required
          variant="outlined"
        />
        <TextField
          {...register('city')}
          {...getFieldProps('city', errors)}
          fullWidth
          label="City"
          required
          variant="outlined"
        />
        <TextField
          {...register('zipcode')}
          {...getFieldProps('zipcode', errors)}
          fullWidth
          label="Zip / Postal Code"
          required
          variant="outlined"
        />
      </Stack>

      {submissionError && (
        <Alert
          severity="error"
          sx={{ my: 1 }}
        >
          <AlertTitle>There was an error submitting the form.</AlertTitle>
          Please try again.
        </Alert>
      )}

      <Stack
        mt={2}
        spacing={2.5}
        alignSelf={{
          mobile: 'initial',
          desktop: 'flex-start',
        }}
        direction={{
          mobile: 'column',
          desktop: 'row',
        }}
      >
        {onCancel && (
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        )}

        <LoadingButton
          variant="contained"
          type="submit"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Submit
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default Form;
